import { useState, useEffect, useRef } from "react";
import "./header.scss";
import { NavLink, useNavigate } from "react-router-dom";
import { FaBars } from "react-icons/fa";
import { getProductList } from "../../API/mangoholidayAPI";
import ClipLoader from "react-spinners/ClipLoader";

const Header = () => {
  const navigate = useNavigate();
  const [internationalToursList, setInternationalToursList] = useState([]);
  const [customizedToursList, setCustomizedToursList] = useState([]);
  const [indiaToursList, setIndiaToursList] = useState([]);
  const [customizedIndiaToursList, setCustomizedIndiaToursList] = useState([]);

  // NEW: Store all tours for the search dropdown
  const [allTours, setAllTours] = useState([]);

  const [activeTourTab, setActiveTourTab] = useState("world");
  const [loading, setLoading] = useState(true);

  const [openDropdown, setOpenDropdown] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  // Search Bar functionality states
  const [query, setQuery] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchContainerRef = useRef(null);

  useEffect(() => {
    async function fetchTours() {
      try {
        const [worldData, indiaData] = await Promise.all([
          getProductList({ ProductType: "World" }),
          getProductList({ ProductType: "India" }),
        ]);

        const worldList = worldData?.ProductList || [];
        const indiaList = indiaData?.ProductList || [];

        // Save all tours for search filtering
        setAllTours([...worldList, ...indiaList]);

        // World Tours - GIT
        const internationalTours = worldList.filter(
          (item) => item.TravelType === "GIT",
        );
        const uniqueGitSectorsWorld = [
          ...new Set(internationalTours.map((item) => item.SectorName)),
        ].sort((a, b) => a.localeCompare(b));
        setInternationalToursList(uniqueGitSectorsWorld);

        // Customized World Tours - FIT
        const customizedTours = worldList.filter(
          (item) => item.TravelType === "FIT",
        );
        const uniqueFitSectorsWorld = [
          ...new Set(customizedTours.map((item) => item.SectorName)),
        ].sort((a, b) => a.localeCompare(b));
        setCustomizedToursList(uniqueFitSectorsWorld);

        // India tours - GIT
        const indiaTours = indiaList.filter(
          (item) => item.TravelType === "GIT",
        );
        const uniqueGitSectorsIndia = [
          ...new Set(indiaTours.map((item) => item.SectorName)),
        ].sort((a, b) => a.localeCompare(b));
        setIndiaToursList(uniqueGitSectorsIndia);

        // Customized India tours - FIT
        const customizedIndiaTours = indiaList.filter(
          (item) => item.TravelType === "FIT",
        );
        const uniqueFitSectorsIndia = [
          ...new Set(customizedIndiaTours.map((item) => item.SectorName)),
        ].sort((a, b) => a.localeCompare(b));
        setCustomizedIndiaToursList(uniqueFitSectorsIndia);
      } catch (err) {
        console.error("Failed to load products in component:", err.message);
      } finally {
        setLoading(false);
      }
    }

    fetchTours();
  }, []);

  const toggleMenu = () => {
    setMenuOpen((prev) => !prev);
    if (menuOpen) setOpenDropdown(null);
  };

  const closeMenu = () => {
    setMenuOpen(false);
    setOpenDropdown(null);
    setIsSearchOpen(false);
  };

  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleDropdown = (name, e) => {
    e.stopPropagation();
    handleScrollToTop();
    setOpenDropdown((prev) => (prev === name ? null : name));
  };

  useEffect(() => {
    const handleDocClick = (e) => {
      setOpenDropdown(null);
      // Close search dropdown if clicked outside
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(e.target)
      ) {
        setIsSearchOpen(false);
      }
    };
    document.addEventListener("click", handleDocClick);
    return () => document.removeEventListener("click", handleDocClick);
  }, []);

  // --- Search Logic ---
  const searchFunction = () => {
    handleScrollToTop();
    setIsSearchOpen(false);
    closeMenu();
    if (query.trim()) {
      navigate(`/search/${query}`);
    } else {
      navigate("/");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      searchFunction();
    }
  };

  const handleTourClick = (tour) => {
    closeMenu();
    handleScrollToTop();
    setIsSearchOpen(false);
    setQuery("");
    navigate(
      `/tour-details/${tour.ProductID}/${tour.ProductCode}/${tour.ProductTitle.toLowerCase().replace(/\s+/g, "-")}`,
    );
  };

  // Determine what to show in the search dropdown
  const filteredTours = query.trim()
    ? allTours.filter(
        (tour) =>
          tour.ProductTitle?.toLowerCase().includes(query.toLowerCase()) ||
          tour.SectorName?.toLowerCase().includes(query.toLowerCase()),
      )
    : allTours.slice(0, 5); // Fallback to "Best Selling" (first 5 tours) when empty

  const displayTours = filteredTours.slice(0, 5);
  const hasMore = filteredTours.length > 5;

  return (
    <header className={`header-container ${menuOpen ? "menu-open" : ""}`}>
      {/* Logo */}
      <div className="header-logo">
        <a href="/">
          <img
            src="/img/logo.webp"
            alt="Mango Holidays Logo"
            className="logo-img"
          />
        </a>
      </div>

      {/* Hamburger Button */}
      <button
        className="menu-toggle"
        onClick={toggleMenu}
        aria-label="Toggle menu"
        aria-expanded={menuOpen}
      >
        <FaBars />
      </button>

      {/* Nav + Search */}
      <nav className={`header-nav-search ${menuOpen ? "show" : ""}`}>
        <ul className="nav-links">
          <li className="nav-item">
            <NavLink
              to="/"
              onClick={() => {
                closeMenu();
                handleScrollToTop();
              }}
              className={({ isActive }) =>
                isActive && openDropdown === null
                  ? "nav-link active"
                  : "nav-link"
              }
            >
              Home
            </NavLink>
          </li>

          <li className="nav-item">
            <NavLink
              to="/all-packages"
              onClick={() => {
                closeMenu();
                handleScrollToTop();
              }}
              className={({ isActive }) =>
                isActive && openDropdown === null
                  ? "nav-link active"
                  : "nav-link"
              }
            >
              Packages
            </NavLink>
          </li>

          <li className="nav-item">
            <NavLink
              to="/about"
              onClick={() => {
                closeMenu();
                handleScrollToTop();
              }}
              className={({ isActive }) =>
                isActive && openDropdown === null
                  ? "nav-link active"
                  : "nav-link"
              }
            >
              About
            </NavLink>
          </li>

          <li className="nav-item">
            <NavLink
              to="/articles"
              onClick={() => {
                closeMenu();
                handleScrollToTop();
              }}
              className={({ isActive }) =>
                isActive && openDropdown === null
                  ? "nav-link active"
                  : "nav-link"
              }
            >
              Articles
            </NavLink>
          </li>

          <li className="nav-item">
            <NavLink
              to="/contact"
              onClick={() => {
                closeMenu();
                handleScrollToTop();
              }}
              className={({ isActive }) =>
                isActive && openDropdown === null
                  ? "nav-link active"
                  : "nav-link"
              }
            >
              Contact Us
            </NavLink>
          </li>
          <li className="nav-item">
            <NavLink
              to="/photo-gallery"
              onClick={() => {
                closeMenu();
                handleScrollToTop();
              }}
              className={({ isActive }) =>
                isActive && openDropdown === null
                  ? "nav-link active"
                  : "nav-link"
              }
            >
              Photo Gallery
            </NavLink>
          </li>

          <li className="nav-item">
            <NavLink
              to="/all-blogs"
              onClick={() => {
                closeMenu();
                handleScrollToTop();
              }}
              className={({ isActive }) =>
                isActive && openDropdown === null
                  ? "nav-link active"
                  : "nav-link"
              }
            >
              Blogs
            </NavLink>
          </li>

          <li className="nav-item">
            <NavLink
              to="/termCondition"
              onClick={() => {
                closeMenu();
                handleScrollToTop();
              }}
              className={({ isActive }) =>
                isActive && openDropdown === null
                  ? "nav-link active"
                  : "nav-link"
              }
            >
              T&C
            </NavLink>
          </li>
        </ul>

        <div className="nav-call-item">
          <a
            href="tel:8380039505"
            className="header-call-btn nav-link"
            onClick={() => {
              closeMenu();
              handleScrollToTop();
            }}
          >
            <div className="call-icon">
              <i className="fa-solid fa-phone"></i>
            </div>
            <div className="call-text">
              <span className="call-title">Call Now</span>
              <span className="call-number">+91 83800 39505</span>
            </div>
          </a>
        </div>

        {/* Search Bar with Dynamic Dropdown */}
        <div className="search-bar-container" ref={searchContainerRef}>
          <input
            type="text"
            placeholder="Search destinations, tours..."
            className="search-input"
            aria-label="Search destination"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setIsSearchOpen(true);
            }}
            onFocus={() => setIsSearchOpen(true)}
            onKeyDown={handleKeyDown}
            maxLength={40}
          />
          <button
            className="search-button"
            onClick={searchFunction}
            aria-label="Search"
          >
            <i className="fa-solid fa-magnifying-glass"></i>
          </button>

          {/* Search Dropdown Panel */}
          {isSearchOpen && (
            <div className="search-dropdown-panel">
              {!query.trim() && (
                <div className="search-dropdown-title">
                  Best Selling Packages
                </div>
              )}

              {displayTours.length > 0 ? (
                <ul className="search-dropdown-list">
                  {displayTours.map((tour) => (
                    <li
                      key={tour.ProductID}
                      onClick={() => handleTourClick(tour)}
                      className="search-dropdown-item"
                    >
                      <h4>{tour.ProductTitle}</h4>
                      <p>
                        {tour.Days} Days / {tour.Nights} Nights •{" "}
                        {tour.TravelType === "GIT"
                          ? "Group Tour"
                          : "Customized Tour"}
                      </p>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="search-dropdown-empty">
                  No packages found for "{query}"
                </div>
              )}

              {hasMore && query.trim() !== "" && (
                <button
                  className="search-dropdown-btn"
                  onClick={searchFunction}
                >
                  See all {filteredTours.length} results
                </button>
              )}
            </div>
          )}
        </div>
      </nav>
    </header>
  );
};

export default Header;
